import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { EnvironmentDef, PropModel } from '../config/environments';
import type { VehicleDef } from '../config/vehicles';

/** One draw call's worth of a model: all of its meshes that share a material, merged. */
export interface ModelBatch {
  geometry: THREE.BufferGeometry;
  material: THREE.Material;
}

export interface GameModels {
  coin: ModelBatch[];
  gasCan: ModelBatch[];
  kmPost: ModelBatch[];
  warningSign: ModelBatch[];
}

const BASE = `${import.meta.env.BASE_URL}assets/models/`;
const loader = new GLTFLoader();
const load = async (file: string) => mergeByMaterial((await loader.loadAsync(BASE + file)).scene);

/** Loads the Kiln-authored GLBs (art/kiln-workspace/assets) the run always needs, merged by material. */
export async function loadGameModels(): Promise<GameModels> {
  const [coin, gasCan, kmPost, warningSign] = await Promise.all([
    load('coin.glb'),
    load('gas-can.glb'),
    load('km-post.glb'),
    load('warning-sign.glb'),
  ]);
  return { coin, gasCan, kmPost, warningSign };
}

const fileCache = new Map<string, Promise<ModelBatch[]>>();
const cached = (file: string) => {
  let pending = fileCache.get(file);
  if (!pending) fileCache.set(file, (pending = load(file)));
  return pending;
};

export interface VehicleModels {
  body: ModelBatch[];
  wheel: ModelBatch[];
}

/** A vehicle's body and wheel (loaded once, cached). */
export async function loadVehicleModels(vehicle: VehicleDef): Promise<VehicleModels> {
  const [body, wheel] = await Promise.all([cached(vehicle.view.body), cached(vehicle.view.wheel)]);
  return { body, wheel };
}

export interface UpgradeParts {
  tyre: ModelBatch[];
  coilover: ModelBatch[];
  rack: ModelBatch[];
  stack: ModelBatch[];
  scoop: ModelBatch[];
}

/** Visible upgrade parts (art/kiln-workspace/assets/upg-*), shared by every vehicle. */
export async function loadUpgradeParts(): Promise<UpgradeParts> {
  const [tyre, coilover, rack, stack, scoop] = await Promise.all(
    ['upg-offroad-tyre.glb', 'upg-coilover.glb', 'upg-roof-rack.glb', 'upg-exhaust.glb', 'upg-hood-scoop.glb'].map(cached),
  );
  return { tyre, coilover, rack, stack, scoop };
}

/** Roadside and landmark models (Kiln GLBs), keyed as environments name them. */
const PROP_FILES: Record<PropModel, string> = {
  palm: 'palm-tree.glb',
  banana: 'banana-plant.glb',
  fence: 'bamboo-fence.glb',
  warung: 'warung.glb',
  school: 'school.glb',
  kopdes: 'kopdes-merah-putih.glb',
  dapur: 'dapur-mbg.glb',
  house: 'kampung-house.glb',
  teaBush: 'tea-bush.glb',
  pine: 'pine-tree.glb',
  rock: 'bromo-rock.glb',
  shrub: 'ash-shrub.glb',
  jukung: 'jukung.glb',
  umbrella: 'beach-umbrella.glb',
  gate: 'candi-bentar.glb',
  lamp: 'street-lamp.glb',
  gerobak: 'gerobak.glb',
};

const propCache = new Map<PropModel, Promise<ModelBatch[]>>();

/** Loads (once) every prop model an environment uses. The start kitchen is always a warung. */
export async function loadPropModels(env: EnvironmentDef): Promise<Partial<Record<PropModel, ModelBatch[]>>> {
  const needed = new Set<PropModel>(['dapur']);
  for (const r of env.props) needed.add(r.model);
  for (const l of env.landmarks) needed.add(l.model);
  if (env.rows) needed.add(env.rows.model);
  const out: Partial<Record<PropModel, ModelBatch[]>> = {};
  await Promise.all(
    [...needed].map(async (model) => {
      let pending = propCache.get(model);
      if (!pending) propCache.set(model, (pending = load(PROP_FILES[model])));
      out[model] = await pending;
    }),
  );
  return out;
}

/**
 * Bakes every mesh's transform (relative to the model root) and merges meshes that share a
 * material, so a Kiln model with dozens of named parts costs one draw call per material.
 */
export function mergeByMaterial(root: THREE.Object3D): ModelBatch[] {
  root.updateMatrixWorld(true);
  const rootInverse = root.matrixWorld.clone().invert();
  const groups = new Map<THREE.Material, THREE.BufferGeometry[]>();
  const local = new THREE.Matrix4();
  root.traverse((node) => {
    const mesh = node as THREE.Mesh;
    if (!mesh.isMesh || Array.isArray(mesh.material)) return;
    local.multiplyMatrices(rootInverse, mesh.matrixWorld);
    // Keep only the attributes every Kiln mesh shares, so the geometries can merge.
    const source = mesh.geometry.index ? mesh.geometry.toNonIndexed() : mesh.geometry.clone();
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', source.getAttribute('position'));
    geometry.setAttribute('normal', source.getAttribute('normal'));
    geometry.applyMatrix4(local);
    source.dispose();
    const list = groups.get(mesh.material) ?? [];
    list.push(geometry);
    groups.set(mesh.material, list);
  });
  const batches: ModelBatch[] = [];
  for (const [material, geometries] of groups) {
    const geometry = mergeGeometries(geometries, false);
    if (!geometry) throw new Error('Could not merge model geometries');
    for (const g of geometries) g.dispose();
    geometry.computeBoundingSphere();
    batches.push({ geometry, material });
  }
  return batches;
}

/** A plain Group of meshes for a model (one mesh per material batch). */
export function instantiate(batches: ModelBatch[], castShadow = true): THREE.Group {
  const group = new THREE.Group();
  for (const b of batches) {
    const mesh = new THREE.Mesh(b.geometry, b.material);
    mesh.castShadow = castShadow;
    group.add(mesh);
  }
  return group;
}

/** One InstancedMesh per material batch of a model, all driven by the same instance matrices. */
export class InstancedModel {
  readonly meshes: THREE.InstancedMesh[];

  constructor(batches: ModelBatch[], capacity: number, parent: THREE.Object3D) {
    this.meshes = batches.map((b) => {
      const mesh = new THREE.InstancedMesh(b.geometry, b.material, capacity);
      mesh.frustumCulled = false;
      mesh.count = 0;
      parent.add(mesh);
      return mesh;
    });
  }

  get capacity(): number {
    return this.meshes[0]?.instanceMatrix.count ?? 0;
  }

  set(i: number, matrix: THREE.Matrix4): void {
    for (const mesh of this.meshes) mesh.setMatrixAt(i, matrix);
  }

  commit(count: number): void {
    for (const mesh of this.meshes) {
      mesh.count = count;
      mesh.instanceMatrix.needsUpdate = true;
    }
  }
}
